import { SchemaGetter } from "effect";
import { Schema as S } from "effect";
import { CardReadLogIdSchema } from "../crlog/card-read-log-id";

const AddressSchema = S.Struct({
  kind: S.String,
  street: S.optional(S.String),
  houseno: S.optional(S.String),
  zip: S.optional(S.String),
  city: S.optional(S.String),
  countryCode: S.optional(S.String),
  addon: S.optional(S.String),
  country: S.optional(S.String),
  title: S.optional(S.String),
  firstname: S.optional(S.String),
  nameaffix: S.optional(S.String),
  middlename: S.optional(S.String),
  lastname: S.optional(S.String),
  _id: S.optional(S.String),
});

const InsuranceStatusSchema = S.Struct({
  type: S.String,
  cardType: S.optional(S.String),
  terminalType: S.optional(S.String),
  cardTypeGeneration: S.optional(S.String),
  cdmVersion: S.optional(S.String),
  fk4108: S.optional(S.String),
  fk4133: S.optional(S.NullOr(S.String)),
  fk4110: S.optional(S.NullOr(S.String)),
  insuranceNo: S.optional(S.String),
  insuranceName: S.optional(S.String),
  insurancePrintName: S.optional(S.String),
  kv: S.optional(S.String),
  insuranceId: S.optional(S.String),
  insuranceGrpId: S.optional(S.String),
  insuranceCountry: S.optional(S.String),
  cardSwipe: S.optional(S.String),
  insuranceKind: S.optional(S.String),
  persGroup: S.optional(S.String),
  dmp: S.optional(S.String),
  costCarrierBillingSection: S.optional(S.String),
  costCarrierBillingGroup: S.optional(S.String),
  locationFeatures: S.optional(S.String),
  abrechnungsbereiche: S.optional(S.Record(S.String, S.String)),
  feeSchedule: S.optional(S.String),
  fused: S.optional(S.Boolean),
  address1: S.optional(S.String),
  address2: S.optional(S.String),
  mainInsuranceId: S.optional(S.String),
  unknownInsurance: S.optional(S.Boolean),
  locationId: S.optional(S.String),
  unzkv: S.optional(S.Array(S.Unknown)),
  bgNumber: S.optional(S.String),
  zipcode: S.optional(S.String),
  phone: S.optional(S.String),
  insuranceLink: S.optional(S.String),
  email: S.optional(S.String),
  insuranceGLN: S.optional(S.String),
  recipientGLN: S.optional(S.String),
  department: S.optional(S.String),
  fk3010: S.optional(S.String),
  fk3011: S.optional(S.String),
  fk3013: S.optional(S.String),
  temporaryEntry: S.optional(S.Boolean),
  isTiersGarant: S.optional(S.Boolean),
  isTiersPayant: S.optional(S.Boolean),
});

const MatchedPatientSchema = S.Struct({
  _id: S.String,
  addresses: S.optional(S.Array(AddressSchema)),
  title: S.optional(S.String),
  firstname: S.optional(S.String),
  nameaffix: S.optional(S.String),
  fk3120: S.optional(S.String),
  lastname: S.optional(S.String),
  kbvDob: S.optional(S.String),
  insuranceStatus: S.optional(S.Array(InsuranceStatusSchema)),
  dob: S.optional(S.String),
  dob_MM: S.optional(S.String),
  dob_DD: S.optional(S.String),
  talk: S.optional(S.String),
  gender: S.optional(S.String),
  patientNo: S.optional(S.String),
  activeCaseFolderId: S.optional(S.String),
});

/**
 * Decode card data once at the vendor boundary, including the empty-match sentinel.
 * @since 1.1.10
 * @example
 * S.decodeUnknownSync(PatientDataSchema)({ firstname: "Erika", matchedPatients: "" });
 */
export const PatientDataSchema = S.Struct({
  _id: S.optional(S.String),
  firstname: S.optional(S.String),
  lastname: S.optional(S.String),
  gender: S.optional(S.String),
  talk: S.optional(S.String),
  kbvDob: S.optional(S.String),
  nameaffix: S.optional(S.String),
  fk3120: S.optional(S.String),
  title: S.optional(S.String),
  addresses: S.optional(S.Array(AddressSchema)),
  insuranceStatus: S.optional(S.Array(InsuranceStatusSchema)),
  dob: S.optional(S.String),
  dob_DD: S.optional(S.String),
  dob_MM: S.optional(S.String),
  countryMode: S.optional(S.Array(S.String)),
  crlogId: S.optional(CardReadLogIdSchema),
  status: S.optional(
    S.Union(
      [S.Literal("READ"),
      S.Literal("PARSED"),
      S.Literal("MATCHED"),
      S.Literal("APPLIED"),
      S.Literal("MATCHING"),
      S.Literal("MERGED"),
      S.Literal("CANCELLED")],
    ),
  ),
  validationStatus: S.optional(S.String),
  matchedPatients: S.optional(S.Union([S.Array(MatchedPatientSchema), S.Literal("").pipe(S.decodeTo(S.Array(MatchedPatientSchema), {  decode: SchemaGetter.transform(() => []), encode: SchemaGetter.transform(() => "" as const) }))])),
  diff: S.optional(
    S.Array(
      S.Struct({
        aVal: S.optional(S.Unknown),
        bVal: S.optional(S.Unknown),
        type: S.optional(S.String),
        changed: S.optional(S.Boolean),
        label: S.optional(S.String),
        path: S.optional(S.String),
        parentPath: S.optional(S.String),
        parentPathLabel: S.optional(S.String),
        index: S.optional(S.Number),
        isEnum: S.optional(S.Boolean),
      }),
    ),
  ),
  possibleActions: S.optional(S.Array(S.String)),
  extraInfo: S.optional(S.String),
}).pipe(S.check(S.makeFilter((patient) => Object.keys(patient).length > 0, {
  message: "Card response contained no recognized fields",
})));

/**
 * Decoded patient-card payload returned by the DocCirrus cardreader API.
 *
 * @example
 * ```ts
 * const patient: PatientData = { firstname: "Erika", lastname: "Mustermann" };
 * ```
 *
 * @since 1.1.7
 */
export type PatientData = S.Schema.Type<typeof PatientDataSchema>;

// inSuite returns an array (unwrapped from { meta, data: [...] })
// Empty array means no card data available
const InitialCardReadSchema = PatientDataSchema.pipe(S.check(S.makeFilter(
  patient => S.is(CardReadLogIdSchema)(patient.crlogId),
  { message: "Card response requires a valid operation identifier" },
)));
export const GetPatientFromCardResponseSchema = S.Union([
  S.Array(InitialCardReadSchema).check(S.makeFilter(
    patients => patients.length <= 1,
    { message: "Card read returned multiple operations" },
  )),
  InitialCardReadSchema,
]);

export type GetPatientFromCardResponse = S.Schema.Type<typeof GetPatientFromCardResponseSchema>;
